import useRequest from "@/lib/request/useRequest";
import { object, string, number, boolean, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import SelectInput from "../inputs/SelectInput";
import NumberInputComponent from "../inputs/NumberInput";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import { addToast, Button } from "@heroui/react";
import { CropConfiguration } from "@/types/cropConfiguration";
import { SoilTexture } from "@/types/soil";
import { CoverCrop, CoverCropTermination, Tillage } from "@/types/enums";
import { CropCycleV2 } from "@/types/cropCycle";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

const schema = object().shape({
    previous_crop: number().nullable(),
    soil_configuration: number().nullable(),
    soil_organic_matter: number().nullable(),
    cover_crop: string().nullable(),
    cover_crop_termination: string().nullable(),
    manure_use: boolean().nullable(),
    inhibitor_use: boolean().nullable(),
    eef_use: boolean().nullable(),
    tillage_practice: string().nullable(),
    has_drainage: boolean().nullable(),
    typical_N_applied_pounds_per_acre: number().nullable().typeError(""),
    typical_yield_bushels_per_acre: number().nullable().typeError(""),
    planned_total_nitrogen: number().nullable().typeError(""),
    yield_goal: number().nullable().typeError(""),
});

type FormType = InferType<typeof schema>;

function SoilManagement({
    fields,
    required,
    onFinish,
}: {
    fields: {
        id: number;
        soil_configuration: number | null | undefined;
        soil_organic_matter: number | null | undefined;
        has_drainage: boolean | null | undefined;
        typical_N_applied_pounds_per_acre?: number | null;
        typical_yield_bushels_per_acre?: number | null;
        crop_cycle:
            | {
                  id?: number | null;
                  previous_crop?: number | null;
                  cover_crop?: string | null;
                  cover_crop_termination?: string | null;
                  manure_use?: boolean | null;
                  inhibitor_use?: boolean | null;
                  eef_use?: boolean | null;
                  tillage_practice?: string | null;
                  planned_total_nitrogen?: number | null;
                  yield_goal?: number | null;
              }
            | undefined
            | null;
    }[];
    required: boolean;
    onFinish: () => void;
}) {
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    const { useGetRequest, useMutateRequest } = useRequest();
    const { setSelectedCropCycle } = useFieldDashboard() ?? {};
    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: "crops/",
    });
    const soilsQuery = useGetRequest<SoilTexture[]>({
        url: "soils/",
    });
    const fieldMutation = useMutateRequest({
        url: "fields/bulk_update/",
        method: "patch",
    });
    const cropCycleMutation = useMutateRequest<CropCycleV2[]>({
        url: "crop_cycles/bulk_upsert/",
        method: "post",
    });

    useEffect(() => {
        if (fields.length === 1) {
            const field = fields[0];
            const cycle = field.crop_cycle;
            form.reset({
                previous_crop: cycle?.previous_crop ?? null,
                soil_configuration: field.soil_configuration ?? null,
                soil_organic_matter: field.soil_organic_matter ?? null,
                cover_crop: cycle?.cover_crop ?? null,
                cover_crop_termination: cycle?.cover_crop_termination ?? null,
                manure_use: cycle?.manure_use ?? null,
                inhibitor_use: cycle?.inhibitor_use ?? null,
                eef_use: cycle?.eef_use ?? null,
                tillage_practice: cycle?.tillage_practice ?? null,
                has_drainage: field.has_drainage ?? null,
                typical_N_applied_pounds_per_acre:
                    field.typical_N_applied_pounds_per_acre ?? null,
                typical_yield_bushels_per_acre:
                    field.typical_yield_bushels_per_acre ?? null,
                planned_total_nitrogen: cycle?.planned_total_nitrogen ?? null,
                yield_goal: cycle?.yield_goal ?? null,
            });
        } else {
            form.reset({
                previous_crop: null,
                soil_configuration: null,
                soil_organic_matter: null,
                cover_crop: null,
                cover_crop_termination: null,
                manure_use: null,
                inhibitor_use: null,
                eef_use: null,
                tillage_practice: null,
                has_drainage: null,
                typical_N_applied_pounds_per_acre: null,
                typical_yield_bushels_per_acre: null,
                planned_total_nitrogen: null,
                yield_goal: null,
            });
        }
    }, [fields, form]);

    const coverCropOptions = Object.values(CoverCrop).map((x) => ({
        id: x,
        description: x,
    }));
    const terminationOptions = Object.values(CoverCropTermination).map((x) => ({
        id: x,
        description: x,
    }));
    const tillageOptions = Object.values(Tillage).map((x) => ({
        id: x,
        description: x,
    }));

    const [coverCrop, hasDrainage, manureUse, inhibitorUse, eefUse] =
        form.watch([
            "cover_crop",
            "has_drainage",
            "manure_use",
            "inhibitor_use",
            "eef_use",
        ]);

    const onSubmit = async (data: FormType) => {
        const filteredData = Object.fromEntries(
            Object.entries(data).filter(
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ([_, value]) => value !== null && value !== undefined,
            ),
        );
        const fieldKeys = [
            "soil_configuration",
            "soil_organic_matter",
            "has_drainage",
            "typical_N_applied_pounds_per_acre",
            "typical_yield_bushels_per_acre",
        ];
        const cropCycleKeys = [
            "previous_crop",
            "cover_crop",
            "cover_crop_termination",
            "manure_use",
            "inhibitor_use",
            "eef_use",
            "tillage_practice",
            "planned_total_nitrogen",
            "yield_goal",
        ];
        const fieldData = Object.fromEntries(
            Object.entries(filteredData).filter(([key]) =>
                fieldKeys.includes(key),
            ),
        );
        const cropCycleData = Object.fromEntries(
            Object.entries(filteredData).filter(([key]) =>
                cropCycleKeys.includes(key),
            ),
        );
        if (
            Object.keys(fieldData).length === 0 &&
            Object.keys(cropCycleData).length === 0
        ) {
            addToast({
                title: "No data to save",
                timeout: 3000,
                color: "danger",
            });
            return;
        }
        if (
            required &&
            (!fieldData.soil_configuration ||
                !fieldData.soil_organic_matter ||
                fieldData.has_drainage === null ||
                !cropCycleData.cover_crop ||
                !cropCycleData.cover_crop_termination ||
                cropCycleData.manure_use === null ||
                cropCycleData.inhibitor_use === null ||
                cropCycleData.eef_use === null ||
                !cropCycleData.tillage_practice ||
                cropCycleData.planned_total_nitrogen === null ||
                cropCycleData.yield_goal === null)
        ) {
            addToast({
                title: "All fields are required",
                timeout: 3000,
                color: "danger",
            });
            return;
        }
        const fieldPayload = fields.map((field) => ({
            id: field.id,
            ...fieldData,
        }));
        const cropCyclePayload = fields.map((field) => ({
            id: field.crop_cycle?.id ?? 0,
            field: field.id,
            ...cropCycleData,
        }));
        const mutations = [];
        if (Object.keys(fieldData).length > 0) {
            mutations.push(
                fieldMutation.mutateAsync({
                    url: "fields/bulk_update/",
                    method: "patch",
                    data: fieldPayload,
                }),
            );
        }
        if (Object.keys(cropCycleData).length > 0) {
            mutations.push(
                cropCycleMutation.mutateAsync(
                    {
                        url: "crop_cycles/bulk_upsert/",
                        method: "post",
                        data: cropCyclePayload,
                    },
                    {
                        onSuccess: (data: CropCycleV2[]) => {
                            if (data[0]) setSelectedCropCycle?.(data[0]);
                        },
                    },
                ),
            );
        }
        try {
            await Promise.all(mutations);
            addToast({
                title: "Soil management updated successfully",
                timeout: 3000,
                color: "success",
            });
            onFinish();
        } catch {
            addToast({
                title: "Failed to update soil management",
                timeout: 3000,
                color: "danger",
            });
        }
    };

    return (
        <FormProvider {...form}>
            <form className="flex flex-col gap-3 mx-2">
                <div className="flex flex-row gap-3 items-center">
                    <NumberInputComponent
                        name="typical_N_applied_pounds_per_acre"
                        label="Typical N"
                        size="sm"
                    />
                    <NumberInputComponent
                        name="typical_yield_bushels_per_acre"
                        label="Typical Yield"
                        size="sm"
                    />
                </div>
                <div className="flex flex-row gap-3 items-center">
                    <NumberInputComponent
                        name="planned_total_nitrogen"
                        label="Planned Total Nitrogen"
                        size="sm"
                    />
                    <NumberInputComponent
                        name="yield_goal"
                        label="Yield Goal"
                        size="sm"
                    />
                </div>
                <div className="flex flex-row gap-3 items-center">
                    <AutoCompleteInput
                        name="previous_crop"
                        label="Previous Crop"
                        options={
                            cropsQuery.data?.map((x) => ({
                                id: x.id,
                                description: x.crop_name,
                            })) ?? []
                        }
                        size="sm"
                    />
                    <SelectInput
                        name="tillage_practice"
                        label="Tillage"
                        options={tillageOptions}
                        size="sm"
                    />
                </div>
                <div className="flex flex-row gap-3 items-center">
                    <AutoCompleteInput
                        name="soil_configuration"
                        label="Soil Texture"
                        options={
                            soilsQuery.data?.map((x) => ({
                                id: x.id,
                                description: x.soil_name,
                            })) ?? []
                        }
                        size="sm"
                    />
                    <NumberInputComponent
                        name="soil_organic_matter"
                        label="Organic Matter"
                        size="sm"
                    />
                </div>
                <div className="flex flex-row gap-3 items-center">
                    <SelectInput
                        name="cover_crop"
                        label="Cover Crop"
                        options={coverCropOptions}
                        size="sm"
                    />
                    <SelectInput
                        name="cover_crop_termination"
                        label="Termination"
                        options={terminationOptions}
                        size="sm"
                        isDisabled={coverCrop === CoverCrop.None || !coverCrop}
                    />
                </div>
                <div className="flex flex-row gap-3 items-center justify-evenly mb-3">
                    <Button
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={
                            hasDrainage === null
                                ? "default"
                                : hasDrainage === false
                                  ? "danger"
                                  : "success"
                        }
                        startContent={
                            hasDrainage !== null &&
                            hasDrainage !== undefined ? (
                                <FontAwesomeIcon
                                    icon={hasDrainage ? faCheck : faXmark}
                                />
                            ) : undefined
                        }
                        onPress={() => {
                            form.setValue("has_drainage", !hasDrainage);
                        }}
                    >
                        Drainage
                    </Button>
                    <Button
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={
                            manureUse === null
                                ? "default"
                                : manureUse === false
                                  ? "danger"
                                  : "success"
                        }
                        startContent={
                            manureUse !== null && manureUse !== undefined ? (
                                <FontAwesomeIcon
                                    icon={manureUse ? faCheck : faXmark}
                                />
                            ) : undefined
                        }
                        onPress={() => {
                            form.setValue("manure_use", !manureUse);
                        }}
                    >
                        Manure Use
                    </Button>
                    <Button
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={
                            inhibitorUse === null
                                ? "default"
                                : inhibitorUse === false
                                  ? "danger"
                                  : "success"
                        }
                        startContent={
                            inhibitorUse !== null &&
                            inhibitorUse !== undefined ? (
                                <FontAwesomeIcon
                                    icon={inhibitorUse ? faCheck : faXmark}
                                />
                            ) : undefined
                        }
                        onPress={() => {
                            form.setValue("inhibitor_use", !inhibitorUse);
                        }}
                    >
                        Inhibitor Use
                    </Button>
                    <Button
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={
                            eefUse === null
                                ? "default"
                                : eefUse === false
                                  ? "danger"
                                  : "success"
                        }
                        startContent={
                            eefUse !== null && eefUse !== undefined ? (
                                <FontAwesomeIcon
                                    icon={eefUse ? faCheck : faXmark}
                                />
                            ) : undefined
                        }
                        onPress={() => {
                            form.setValue("eef_use", !eefUse);
                        }}
                    >
                        EEF Use
                    </Button>
                </div>
            </form>
            <Button
                color="primary"
                onPress={() => {
                    form.handleSubmit(onSubmit)();
                }}
                isLoading={
                    fieldMutation.isPending || cropCycleMutation.isPending
                }
                fullWidth
            >
                Save
            </Button>
        </FormProvider>
    );
}

export default SoilManagement;
